/**
 * AppHeader — the authenticated app shell header.
 *
 * Distinct from the public Header.tsx — this one:
 *   - Shows the uhealth-logo.svg + divider + "Innovation Pipeline" subtitle.
 *   - Has a hamburger toggle (onToggleSidebar prop) for mobile.
 *   - Shows an avatar menu with user name and sign-out.
 *   - Shows a role badge (brand for admin, informative for reviewer).
 *
 * Uses Fluent UI v9 primitives + makeStyles/tokens only. No new SCSS.
 */

import {
  Avatar,
  Badge,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuItemSwitch,
  MenuDivider,
  Persona,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Hamburger } from '@fluentui/react-nav';
import { SignOut20Regular, WeatherMoon20Regular } from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { useUserPhoto } from '@/shared/hooks/useUserPhoto';
import { useMyProfile } from '@/shared/hooks/useMyProfile';
import { useThemeMode } from '@/shared/providers/ThemeProvider';
import { BrandLockup } from './BrandLockup';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AppHeaderProps {
  onToggleSidebar?: () => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  header: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
  },
  headerInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '64px',
    width: '100%',
    maxWidth: '1320px',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
    paddingLeft: '24px',
    paddingRight: '24px',
  },
  brandGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  avatarButton: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    minWidth: 'auto',
    maxWidth: 'none',
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    backgroundColor: 'transparent',
  },
  menuPersona: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppHeader({ onToggleSidebar }: AppHeaderProps) {
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const user = useCurrentUser();
  const { data: photoUrl } = useUserPhoto(accounts[0]?.localAccountId);
  const { data: profile } = useMyProfile(accounts.length > 0);
  const { mode, setMode } = useThemeMode();

  const handleSignOut = async () => {
    // Sign out of the application only — clear the local MSAL token cache
    // rather than logoutRedirect(), which would also end the Microsoft SSO
    // session for the account across every app.
    try {
      const account = instance.getActiveAccount();
      await instance.clearCache({ account: account ?? undefined });
      window.location.href = window.location.origin;
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerInner}>
        <div className={styles.brandGroup}>
          {onToggleSidebar && (
            <Hamburger onClick={onToggleSidebar} aria-label="Toggle navigation" />
          )}
          <BrandLockup
            to="/dashboard"
            subtitle="External AI Product Intake"
            shortSubtitle="AI Intake"
          />
        </div>

        <div className={styles.actions}>
          {user && (
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="transparent"
                  className={styles.avatarButton}
                  aria-label="User menu"
                >
                  {user.role && (
                    <Badge
                      appearance="filled"
                      color={user.role === 'admin' ? 'brand' : 'informative'}
                      shape="rounded"
                    >
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </Badge>
                  )}
                  <Avatar
                    name={user.name}
                    color="colorful"
                    idForColor={user.email || undefined}
                    image={photoUrl ? { src: photoUrl } : undefined}
                  />
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList
                  checkedValues={{ theme: mode === 'dark' ? ['dark'] : [] }}
                  onCheckedValueChange={(_, data) => {
                    if (data.name === 'theme') {
                      setMode(data.checkedItems.includes('dark') ? 'dark' : 'light');
                    }
                  }}
                >
                  <div className={styles.menuPersona}>
                    <Persona
                      name={user.name}
                      secondaryText={user.email}
                      tertiaryText={profile?.jobTitle || undefined}
                      avatar={{
                        size: 56,
                        color: 'colorful',
                        idForColor: user.email || undefined,
                        image: photoUrl ? { src: photoUrl } : undefined,
                      }}
                    />
                  </div>
                  <MenuDivider />
                  <MenuItemSwitch
                    icon={<WeatherMoon20Regular />}
                    name="theme"
                    value="dark"
                    persistOnClick
                  >
                    Dark mode
                  </MenuItemSwitch>
                  <MenuDivider />
                  <MenuItem icon={<SignOut20Regular />} onClick={handleSignOut}>
                    Sign out
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
        </div>
      </div>
    </header>
  );
}

export default AppHeader;
